import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { auth } from '../../firebase';
import { TextInput, Alert } from 'flowbite-react';
import { FcGoogle } from 'react-icons/fc';
import { ChevronLeft } from 'lucide-react';
import { signInWithApple } from '../../services/authService';
import { FaApple } from 'react-icons/fa';

const AppSignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for redirect result when component mounts
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          navigate('/');
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  }, [navigate]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setIsLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      // Add a check to see if popstate event variable exists in window and clean it up if necessary
      if (window.dispatchEvent && typeof window.__lastPopStateEvent !== 'undefined') {
        delete window.__lastPopStateEvent;
      }
      
      await signInWithRedirect(auth, provider);
      // The redirect will happen here, and the result will be handled in useEffect
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAppleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Log the environment
      console.log('Environment check for Apple Sign-Up:', {
        isUmmahtiNative: window.isUmmahtiNative,
        hasUmmahti: Boolean(window.ummahti),
        hasAppleSignIn: Boolean(window.ummahti?.signInWithApple)
      });

      // Add a check to see if popstate event variable exists in window and clean it up if necessary
      if (window.dispatchEvent && typeof window.__lastPopStateEvent !== 'undefined') {
        delete window.__lastPopStateEvent;
      }

      await signInWithApple();
      navigate('/');
    } catch (error) {
      console.error('Apple Sign-Up error:', error);
      // Make error message more user-friendly
      if (error.code === 'auth/popup-blocked') {
        setError('Please enable popups for this website to sign up with Apple');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Sign-up was cancelled');
      } else if (error.message && error.message.includes('AuthenticationServices')) {
        // Handle native Apple authentication errors
        if (error.message.includes('error 1000')) {
          setError('Apple Sign-Up was cancelled');
        } else if (error.message.includes('error 1001')) {
          setError('Apple Sign-Up failed: Invalid parameters');
        } else if (error.message.includes('error -3')) {
          setError('Unable to connect to Apple services. Please check your internet connection.');
        } else {
          // Generic message for other AuthenticationServices errors
          setError('Apple Sign-Up failed. Please try again or use another sign-up method.');
        }
      } else {
        // For other errors, provide a cleaner message
        setError('Unable to sign up with Apple. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen relative overflow-hidden bg-[#0A0A0A]">
      {/* Back Button */}
      <div className="absolute top-0 left-0 z-10" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)', paddingLeft: '1rem' }}>
        <Link
          to="/"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20"
        >
          <ChevronLeft className="text-white" size={20} />
        </Link>
      </div>

      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Image Layer */}
        <img
          src="/images/headerbg.png"
          alt=""
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] max-w-none h-full object-cover opacity-30 object-top"
        />
        {/* Primary Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/95 to-[#111111]" />
        {/* Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-emerald-500/[0.03] to-transparent" />
      </div>

      {/* Gradient Orbs */}
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 bg-emerald-500/[0.07]" />
      <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 bg-emerald-500/[0.07]" />

      {/* Content */}
      <div className="relative flex flex-col items-center justify-center flex-1 px-8 mt-10 overflow-y-auto" 
           style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
        <div className="w-full max-w-sm bg-transparent">
          <div>
            <h2 className="text-2xl [@media(max-height:800px)]:text-xl [@media(max-height:650px)]:text-lg font-light mb-6 [@media(max-height:800px)]:mb-4 [@media(max-height:650px)]:mb-3 text-center text-white">
              Create an account
            </h2>
            
            {error && (
              <Alert 
                color="failure" 
                className="mb-6 [@media(max-height:800px)]:mb-4 [@media(max-height:650px)]:mb-3 [@media(max-height:650px)]:text-xs"
                onDismiss={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSignUp} className="space-y-4 [@media(max-height:800px)]:space-y-3 [@media(max-height:650px)]:space-y-2">
              <div>
                <TextInput
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [@media(max-height:800px)]:[&>input]:py-3 [@media(max-height:650px)]:[&>input]:py-2 [&>input]:backdrop-blur-md"
                />
              </div>
              <div>
                <TextInput
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [@media(max-height:800px)]:[&>input]:py-3 [@media(max-height:650px)]:[&>input]:py-2 [&>input]:backdrop-blur-md"
                />
              </div>
              <div>
                <TextInput
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [@media(max-height:800px)]:[&>input]:py-3 [@media(max-height:650px)]:[&>input]:py-2 [&>input]:backdrop-blur-md"
                />
              </div>
              <button
                type="submit" 
                className="w-full py-4 [@media(max-height:800px)]:py-3 [@media(max-height:650px)]:py-2 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-emerald-500 hover:bg-emerald-400 text-black font-medium [@media(max-height:650px)]:text-sm"
              >
                Sign up
              </button>
            </form>

            <div className="relative my-6 [@media(max-height:800px)]:my-4 [@media(max-height:650px)]:my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-xs [@media(max-height:650px)]:text-[10px] uppercase">
                <span className="px-2 backdrop-blur-md text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="space-y-3 [@media(max-height:800px)]:space-y-2">
              <button
                className="w-full py-4 [@media(max-height:800px)]:py-3 [@media(max-height:650px)]:py-2 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-white/10 hover:bg-white/20 text-white border border-white/[0.05] [@media(max-height:650px)]:text-sm"
                onClick={handleGoogleSignUp}
                disabled={isLoading}
              >
                <div className="flex items-center justify-center">
                  <FcGoogle className="mr-2 h-5 w-5 [@media(max-height:650px)]:h-4 [@media(max-height:650px)]:w-4" />
                  {isLoading ? 'Signing up...' : 'Sign up with Google'}
                </div>
              </button>
              
              <button
                className="w-full py-4 [@media(max-height:800px)]:py-3 [@media(max-height:650px)]:py-2 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-black text-white border border-white/[0.05] [@media(max-height:650px)]:text-sm"
                onClick={handleAppleSignUp}
                disabled={isLoading}
              >
                <div className="flex items-center justify-center">
                  <FaApple className="mr-2 h-5 w-5 [@media(max-height:650px)]:h-4 [@media(max-height:650px)]:w-4" />
                  {isLoading ? 'Signing up...' : 'Sign up with Apple'}
                </div>
              </button>
            </div>

            <p className="mt-6 [@media(max-height:800px)]:mt-4 [@media(max-height:650px)]:mt-3 text-center text-sm [@media(max-height:650px)]:text-xs text-gray-400">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-emerald-400 hover:text-emerald-300"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Footer Text */}
      <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
        <p className="text-sm [@media(max-height:650px)]:text-xs text-gray-400">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-emerald-400 hover:text-emerald-300">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AppSignUp; 